import { useEffect, useRef, useState } from "react";
import { ChatCircle, CheckCircle, Eraser, FileCsv, PencilSimple, Plus, SpinnerGap, Star, Trash, Wrench, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIES, categoryByKey, formatDateShort } from "@/lib/constants";
import { toast } from "@/components/ui/sonner";

const EMPTY = { category: "", name: "", description: "", phone: "", email: "", alert_categories: [], specialites: [], is_cleaning_provider: false };
const SPECIALTY_LABELS = { ascenseur: "Ascenseur", chauffage: "Chauffage", porte_garage: "Porte automatique", serrurerie: "Serrurerie", electricite: "Électricité", plomberie: "Plomberie" };

export default function AdminServices() {
  const [services, setServices] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const pickCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = await api.post("/admin/services/import", text, { headers: { "Content-Type": "text/csv" } });
      await load();
      if (data.errors?.length) {
        toast.warning(
          `${data.created} prestataire(s) importé(s) — ${data.errors.length} ligne(s) ignorée(s) (ex. ligne ${data.errors[0].line} : ${data.errors[0].reason})`
        );
      } else {
        toast.success(`${data.created} prestataire(s) importé(s)`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setImporting(false);
  };
  const [confirmReset, setConfirmReset] = useState(null);
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [savingReply, setSavingReply] = useState(null);

  const saveReply = async (r) => {
    const text = (replyDrafts[r.id] ?? r.response ?? "").trim();
    setSavingReply(r.id);
    try {
      await api.patch(`/admin/reviews/${r.id}/response`, { response: text });
      toast.success(text ? "Réponse publiée sous l'avis" : "Réponse retirée");
      setReplyDrafts((d) => ({ ...d, [r.id]: undefined }));
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSavingReply(null);
  };

  const resetRatings = async (s) => {
    try {
      const { data } = await api.delete(`/admin/services/${s.id}/ratings`);
      toast.success(`${s.name} : ${data.deleted} note(s) et avis effacés`);
      load();
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetAllRatings = async () => {
    try {
      const { data } = await api.delete("/admin/ratings");
      toast.success(`${data.deleted} note(s) et avis effacés pour tous les prestataires`);
      load();
      loadReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = () => api.get("/admin/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => {
    load();
    loadReviews();
  }, []);

  const approveReview = async (r) => {
    try {
      await api.post(`/admin/reviews/${r.id}/approve`);
      setReviews((list) => list.filter((x) => x.id !== r.id));
      toast.success("Avis publié sur la page Services");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (r) => {
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      setReviews((list) => list.filter((x) => x.id !== r.id));
      toast.success("Avis supprimé (la note en étoiles est conservée)");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payload = () => ({
    category: form.category,
    name: form.name,
    description: form.description,
    phone: form.phone,
    email: form.email || null,
    alert_categories: form.alert_categories,
    specialites: form.specialites,
    is_cleaning_provider: form.is_cleaning_provider,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.category.trim()) {
      toast.error("Nom, catégorie et téléphone sont requis");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await api.patch(`/admin/services/${editingId}`, payload());
        setServices((list) => list.map((s) => (s.id === editingId ? data : s)));
        toast.success(`${data.name} mis à jour`);
      } else {
        const { data } = await api.post("/admin/services", payload());
        setServices((s) => [...(s || []), data]);
        toast.success(`${data.name} ajouté aux prestataires`);
      }
      setForm(EMPTY);
      setEditingId(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      category: s.category,
      name: s.name,
      description: s.description || "",
      phone: s.phone || "",
      email: s.email || "",
      alert_categories: s.alert_categories || [],
      specialites: s.specialites || [],
      is_cleaning_provider: !!s.is_cleaning_provider,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const remove = async (s) => {
    try {
      await api.delete(`/admin/services/${s.id}`);
      setServices((list) => list.filter((x) => x.id !== s.id));
      if (editingId === s.id) cancelEdit();
      toast.success(`${s.name} retiré des prestataires`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <div data-testid="admin-services-tab">
      {reviews.length > 0 && (
        <div className="mb-6 rounded-3xl border border-black/5 bg-white p-5" data-testid="pending-reviews">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
            <Star size={20} weight="fill" className="text-[#E8A33D]" /> Avis des résidents ({reviews.length})
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Validez les nouveaux commentaires pour les publier, et répondez publiquement aux avis — votre réponse s'affiche sur la page Services, sous le commentaire.
          </p>
          <div className="mt-4 space-y-3">
            {reviews.map((r) => (
              <div
                key={r.id}
                className={`rounded-2xl border p-4 ${r.comment_approved ? "border-black/5 bg-black/[0.015]" : "border-[#E8A33D]/40 bg-[#FDF6E9]"}`}
                data-testid={`pending-review-${r.id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold">{r.service_name}</p>
                  <span className="flex gap-px">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} weight={n <= r.rating ? "fill" : "regular"} className={n <= r.rating ? "text-[#E8A33D]" : "text-black/20"} />
                    ))}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{formatDateShort(r.at)}</span>
                  <span
                    className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${r.comment_approved ? "bg-primary/10 text-primary" : "bg-[#E8A33D]/15 text-[#9C6A1B]"}`}
                    data-testid={`review-state-${r.id}`}
                  >
                    {r.comment_approved ? "Publié" : "En attente"}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80">« {r.comment} »</p>
                {r.response && (
                  <p className="mt-2 rounded-xl bg-primary/5 px-3 py-2 text-xs text-foreground/70" data-testid={`review-response-${r.id}`}>
                    <span className="font-bold text-primary">Votre réponse :</span> {r.response}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    value={replyDrafts[r.id] ?? r.response ?? ""}
                    onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                    placeholder="Votre réponse publique (optionnelle)…"
                    data-testid={`review-reply-input-${r.id}`}
                    className="min-w-48 flex-1 rounded-full border border-black/10 bg-white px-4 py-2 text-xs outline-none transition-colors focus:border-primary"
                  />
                  <button
                    onClick={() => saveReply(r)}
                    disabled={savingReply === r.id}
                    data-testid={`review-reply-save-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
                  >
                    {savingReply === r.id ? <SpinnerGap size={13} className="animate-spin" /> : <ChatCircle size={13} weight="bold" />}
                    {r.response ? "Modifier la réponse" : "Répondre"}
                  </button>
                  {!r.comment_approved && (
                    <button
                      onClick={() => approveReview(r)}
                      data-testid={`review-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#007A4D] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#00925E]"
                    >
                      <CheckCircle size={14} weight="bold" /> Valider et publier
                    </button>
                  )}
                  <button
                    onClick={() => deleteReview(r)}
                    data-testid={`review-delete-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold text-foreground/60 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                  >
                    <Trash size={14} weight="duotone" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
      <form onSubmit={submit} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {editingId ? "Modifier le prestataire" : "Ajouter un prestataire"}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Chaque copropriété a sa propre liste de prestataires.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-black/15 p-3" data-testid="service-import-block">
          <button
            type="button"
            onClick={() => importRef.current?.click()}
            disabled={importing}
            data-testid="service-import-button"
            className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
          >
            {importing ? <SpinnerGap size={14} className="animate-spin" /> : <FileCsv size={14} weight="duotone" />}
            Importer un fichier CSV
          </button>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Colonnes : catégorie ; nom ; description ; téléphone ; email — séparateur « ; » ou « , », avec ou sans ligne d'en-tête.
          </p>
          <input ref={importRef} type="file" accept=".csv,text/csv" hidden data-testid="service-import-input" onChange={pickCsv} />
        </div>
        <div className="mt-5 space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Hydro+ Dépannage)" data-testid="service-name-input" className={inputCls} />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Catégorie (ex. Plomberie)" data-testid="service-category-input" className={inputCls} />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" data-testid="service-description-input" className={inputCls} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (ex. 03 67 10 42 18)" data-testid="service-phone-input" className={inputCls} />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email du prestataire (optionnel)" data-testid="service-email-input" className={inputCls} />
          <div>
            <p className="px-1 text-xs font-bold text-foreground">
              Pastilles d'entretien <span className="font-normal text-muted-foreground">(filtre les listes des Réglages et le choix proposé au résident — plusieurs choix possibles)</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="service-specialites">
              {["ascenseur", "chauffage", "porte_garage", "serrurerie", "electricite", "plomberie"].map((k) => {
                const active = form.specialites.includes(k);
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        specialites: active ? f.specialites.filter((x) => x !== k) : [...f.specialites, k],
                      }))
                    }
                    data-testid={`service-specialite-${k}`}
                    aria-pressed={active}
                    className={
                      active
                        ? "rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground transition-colors"
                        : "rounded-full border border-black/10 px-3 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                    }
                  >
                    {SPECIALTY_LABELS[k]}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="px-1 text-xs font-bold text-foreground">
              Alerté par email pour <span className="font-normal text-muted-foreground">(plusieurs choix possibles)</span>
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="service-alert-categories">
              {CATEGORIES.map((c) => {
                const active = form.alert_categories.includes(c.key);
                return (
                  <button
                    type="button"
                    key={c.key}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        alert_categories: active
                          ? f.alert_categories.filter((k) => k !== c.key)
                          : [...f.alert_categories, c.key],
                      }))
                    }
                    data-testid={`service-alert-chip-${c.key}`}
                    aria-pressed={active}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "border border-black/10 bg-white text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 px-1 text-[11px] text-muted-foreground">
              À chaque signalement de l'un de ces types, le prestataire reçoit un email (gestionnaire en copie, concierge en copie cachée). L'email du prestataire est requis.
            </p>
          </div>
          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-black/10 px-4 py-3" data-testid="service-cleaning-row">
            <div>
              <p className="text-xs font-bold">Prestataire de nettoyage</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Reçoit le rappel automatique si aucun passage d'entretien n'est enregistré depuis le délai choisi dans Réglages.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.is_cleaning_provider}
              data-testid="service-cleaning-toggle"
              onClick={() => setForm((f) => ({ ...f, is_cleaning_provider: !f.is_cleaning_provider }))}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${form.is_cleaning_provider ? "bg-primary" : "bg-black/15"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ${form.is_cleaning_provider ? "left-6" : "left-1"}`} />
            </button>
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="service-add-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
          >
            {saving ? <SpinnerGap size={16} className="animate-spin" /> : editingId ? <PencilSimple size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
            {editingId ? "Enregistrer les modifications" : "Ajouter un prestataire"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="service-cancel-edit"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-5 py-3.5 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <X size={14} weight="bold" /> Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3" data-testid="admin-services-list">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (confirmResetAll) {
                setConfirmResetAll(false);
                resetAllRatings();
              } else {
                setConfirmResetAll(true);
              }
            }}
            data-testid="ratings-reset-all-button"
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
              confirmResetAll
                ? "border-[#C90A0A] bg-[#C90A0A] text-white"
                : "border-black/10 text-muted-foreground hover:border-[#C90A0A] hover:text-[#C90A0A]"
            }`}
          >
            <Eraser size={13} weight="duotone" />
            {confirmResetAll ? "Confirmer : effacer toutes les notes et avis ?" : "Effacer toutes les notes et avis"}
          </button>
        </div>
        {services === null && <div className="h-24 animate-pulse rounded-3xl bg-black/5" />}
        {services !== null && services.length === 0 && (
          <p className="rounded-3xl border border-black/5 bg-white p-8 text-center text-sm text-muted-foreground">
            Aucun prestataire. Ajoutez le premier avec le formulaire.
          </p>
        )}
        {services?.map((s) => (
          <div key={s.id} className="flex items-center gap-4 rounded-3xl border border-black/5 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`admin-service-${s.id}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Wrench size={18} weight="duotone" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">
                {s.name} <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-secondary-foreground">{s.category}</span>
                {(s.alert_categories || []).map((k) => (
                  <span key={k} className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary" data-testid={`service-alert-chip-${s.id}-${k}`}>
                    Alerte : {categoryByKey(k).label}
                  </span>
                ))}
                {(s.specialites || []).map((k) => (
                  <span key={k} className="ml-1 rounded-full bg-[#E8A33D]/15 px-2 py-0.5 text-[10px] font-bold text-[#9C6A1B]" data-testid={`service-specialite-chip-${s.id}-${k}`}>
                    {SPECIALTY_LABELS[k] || k}
                  </span>
                ))}
                {s.is_cleaning_provider && (
                  <span className="ml-1 rounded-full bg-[#E2F6ED] px-2 py-0.5 text-[10px] font-bold text-[#007A4D]" data-testid={`service-cleaning-badge-${s.id}`}>
                    Nettoyage
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {[s.phone, s.email, s.description].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-1 flex items-center gap-0.5" data-testid={`service-rating-${s.id}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={11} weight={n <= Math.round(s.rating_avg || 0) ? "fill" : "regular"} className={n <= Math.round(s.rating_avg || 0) ? "text-[#E8A33D]" : "text-black/20"} />
                ))}
                <span className="ml-1 text-[11px] font-semibold text-muted-foreground">
                  {(s.rating_count || 0) > 0
                    ? `${String(s.rating_avg).replace(".", ",")}/5 · ${s.rating_count} avis`
                    : "Aucune note"}
                </span>
              </p>
            </div>
            <button
              onClick={() => startEdit(s)}
              data-testid={`service-edit-${s.id}`}
              aria-label={`Modifier ${s.name}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
            >
              <PencilSimple size={16} weight="duotone" />
            </button>
            <button
              onClick={() => {
                if (confirmReset === s.id) {
                  setConfirmReset(null);
                  resetRatings(s);
                } else {
                  setConfirmReset(s.id);
                }
              }}
              data-testid={`service-reset-ratings-${s.id}`}
              aria-label={`Effacer les notes et avis de ${s.name}`}
              title="Effacer les notes et avis (2 clics)"
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors ${
                confirmReset === s.id
                  ? "border-[#C90A0A] bg-[#C90A0A] text-white"
                  : "border-black/10 text-foreground/50 hover:border-[#E8A33D] hover:text-[#E8A33D]"
              }`}
            >
              <Eraser size={16} weight="duotone" />
            </button>
            <button
              onClick={() => remove(s)}
              data-testid={`service-delete-${s.id}`}
              aria-label={`Supprimer ${s.name}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
            >
              <Trash size={16} weight="duotone" />
            </button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
